/**
 * L'administration des membres — réservée à l'administrateur.
 *
 * *Demande du métier, 14/08/2026 : « avoir un menu administration des membres spécifique pour
 * l'admin général, avec l'ensemble des membres, décider du statut admin/membre/utilisateur ».*
 *
 * Trois choses, dans l'ordre où l'administrateur les fait :
 *
 *  1. **les demandes en attente**, en tête — c'est ce qu'il vient faire ici, et une demande noyée
 *     au milieu de trente lignes attend des jours ;
 *  2. **la liste complète**, où le rôle et le statut se changent ;
 *  3. **l'invitation**, avec la liste des personnes invitables — celles dont l'adresse du
 *     référentiel ne sert qu'à elles.
 *
 * ## Ce que l'écran ne cache pas
 *
 * Les personnes que le référentiel refuse ne sont pas proposées, mais l'écran **dit combien** :
 * sans ce compte, un administrateur qui cherche un chauffeur dans la liste et ne le trouve pas
 * conclut que l'application est cassée. C'est une adresse de service partagée, et il faut que
 * cela se lise.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import {
  annulerInvitation,
  approuverMembre,
  chargerInvitables,
  chargerInvitations,
  chargerMembres,
  inviter,
  modifierMembre,
} from "./api";
import type { Compte, Invitable, InvitationSuivie, Role, Statut } from "./api";
import { Responsables } from "./Responsables";
import { DestinatairesSav } from "./DestinatairesSav";

const LIBELLE_ROLE: Record<Role, string> = {
  admin: "Administrateur",
  membre: "Membre",
  utilisateur: "Utilisateur",
  magasin: "Magasin",
  be: "BE",
};

const LIBELLE_STATUT: Record<Statut, string> = {
  en_attente: "En attente d'approbation",
  actif: "Actif",
  suspendu: "Suspendu",
};

/** Ce que chaque rôle permet, en une phrase. Un choix sans explication se fait au hasard. */
const EXPLICATION_ROLE: Record<Role, string> = {
  admin: "Administre les membres et les référentiels, en plus de tout le reste.",
  membre: "Travaille sur les contre-études et les plannings.",
  utilisateur: "Le plus restreint. Les droits d'accès détaillés restent à définir.",
  // Le seul rôle qui ne voit PAS le reste de l'application : ce n'est pas une nuance de droits,
  // c'est une autre application. À dire ici, sinon on le posera en croyant restreindre un peu.
  magasin:
    "N'ouvre QUE la gestion des stocks et le référentiel des articles. Ni chantiers, ni " +
    "contre-études, ni plannings.",
  // Le bureau d'études (096). Dire en clair que les menus cachés ne ferment rien : sans quoi on
  // le choisira pour restreindre quelqu'un, ce qu'il ne fait pas (Chris, 27/10/2026).
  be:
    "Bureau d'études : voit toutes les études (codes ETUDE…) et n'affiche que quatre menus — " +
    "Importer un déboursé, Contre-étude, Planning, Chantiers. Les autres menus sont cachés, " +
    "pas interdits.",
};

function formaterDate(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear())}`;
}

export function Membres({ moi }: { moi: Compte }): JSX.Element {
  const [membres, setMembres] = useState<readonly Compte[] | null>(null);
  const [invitables, setInvitables] = useState<readonly Invitable[] | null>(null);
  const [invitations, setInvitations] = useState<readonly InvitationSuivie[]>([]);
  const [erreurInvitables, setErreurInvitables] = useState<string | null>(null);
  const [enAttente, setEnAttente] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  /** Le lien à transmettre à la main, quand aucune messagerie n'est configurée. */
  const [lien, setLien] = useState<string | null>(null);
  const [choisi, setChoisi] = useState("");
  const [roleInvite, setRoleInvite] = useState<Role>("membre");

  const recharger = (): void => {
    chargerMembres()
      .then((r) => {
        setMembres(r.membres);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
    chargerInvitations()
      .then((r) => {
        setInvitations(r.invitations);
      })
      .catch(() => {
        // Le suivi des invitations est un confort : son echec ne doit pas masquer la liste
        // des membres, qui est l ecran principal.
        setInvitations([]);
      });
    // Le référentiel peut ne pas répondre sans que l'écran des membres soit inutilisable : son
    // échec est affiché à côté de la liste d'invitation, pas à la place de tout l'écran.
    chargerInvitables()
      .then((r) => {
        setInvitables(r.invitables);
        setErreurInvitables(null);
      })
      .catch((x: unknown) => {
        setErreurInvitables(x instanceof Error ? x.message : String(x));
      });
  };

  useEffect(recharger, []);

  const agir = (promesse: Promise<unknown>, id: string): void => {
    setEnAttente(id);
    setErreur(null);
    setMessage(null);
    promesse
      .then(recharger)
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setEnAttente(null);
      });
  };

  const enAttenteApprobation = (membres ?? []).filter((m) => m.statut === "en_attente");

  return (
    <section className="referentiel">
      <h2>Membres{membres === null ? "" : ` — ${String(membres.length)}`}</h2>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
      {message !== null && <p className="etat">{message}</p>}

      {lien !== null && (
        <div className="acces__lien">
          <p className="alerte">
            Aucune messagerie n'est configurée : <strong>transmettez ce lien vous-même</strong> à la
            personne concernée, et à personne d'autre — il vaut création de compte.
          </p>
          <code>{lien}</code>
          <button
            type="button"
            className="lien"
            onClick={() => {
              setLien(null);
            }}
          >
            Masquer
          </button>
        </div>
      )}

      {/* Les demandes en attente, en tête et séparées : c'est le geste que l'écran existe pour
          rendre possible. */}
      {enAttenteApprobation.length > 0 && (
        <>
          <h3>
            {enAttenteApprobation.length} demande
            {enAttenteApprobation.length > 1 ? "s" : ""} d'accès à approuver
          </h3>
          <p className="note">
            Choisir le rôle en approuvant : c'est maintenant qu'on sait ce que la personne viendra
            faire. Il reste modifiable ensuite.
          </p>
          <table className="ressources">
            <thead>
              <tr>
                <th>Personne</th>
                <th>Adresse</th>
                <th>Inscrit le</th>
                <th>Approuver comme</th>
              </tr>
            </thead>
            <tbody>
              {enAttenteApprobation.map((m) => (
                <tr key={m.id}>
                  <td className="designation">
                    {m.prenom} {m.nom}
                  </td>
                  <td className="code">{m.courriel}</td>
                  <td>{formaterDate(m.creeLe)}</td>
                  <td className="gestes">
                    {(Object.keys(LIBELLE_ROLE) as Role[]).map((r) => (
                      <button
                        key={r}
                        type="button"
                        className="lien"
                        disabled={enAttente === m.id}
                        title={EXPLICATION_ROLE[r]}
                        onClick={() => {
                          agir(approuverMembre(m.id, r), m.id);
                        }}
                      >
                        {LIBELLE_ROLE[r]}
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h3>Tous les membres</h3>
      {membres === null ? (
        <p>Chargement…</p>
      ) : (
        <table className="ressources">
          <thead>
            <tr>
              <th>Personne</th>
              <th>Adresse</th>
              <th>Rôle</th>
              <th>Statut</th>
              <th>Dernière connexion</th>
            </tr>
          </thead>
          <tbody>
            {membres.map((m) => (
              <tr key={m.id} className={m.statut === "suspendu" ? "retiree" : undefined}>
                <td className="designation">
                  {m.prenom} {m.nom}
                  {m.id === moi.id && <span className="note"> (vous)</span>}
                </td>
                <td className="code">{m.courriel}</td>
                <td>
                  <select
                    className="saisie"
                    value={m.role}
                    disabled={enAttente === m.id}
                    onChange={(e) => {
                      agir(modifierMembre(m.id, { role: e.target.value as Role }), m.id);
                    }}
                  >
                    {(Object.keys(LIBELLE_ROLE) as Role[]).map((r) => (
                      <option key={r} value={r} title={EXPLICATION_ROLE[r]}>
                        {LIBELLE_ROLE[r]}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="saisie"
                    value={m.statut}
                    disabled={enAttente === m.id}
                    onChange={(e) => {
                      agir(modifierMembre(m.id, { statut: e.target.value as Statut }), m.id);
                    }}
                  >
                    {(Object.keys(LIBELLE_STATUT) as Statut[]).map((s) => (
                      <option key={s} value={s}>
                        {LIBELLE_STATUT[s]}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{formaterDate(m.derniereConnexion)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="note">
        Suspendre ferme <strong>immédiatement</strong> les sessions ouvertes de la personne. Un
        compte suspendu n'est pas effacé : ce qu'il a fait reste au journal.
      </p>

      {/* Le SUIVI des invitations. Sans lui, une invitation envoyee disparaissait de
          l ecran des qu on rechargeait : impossible de savoir qui attendait, ni s il
          fallait relancer. Signale par Chris le 14/08/2026. */}
      {invitations.length > 0 && (
        <>
          <h3>Invitations en cours — {invitations.length}</h3>
          <p className="note">
            Ces personnes ont recu un lien et n ont pas encore cree leur compte. Une invitation
            vaut <strong>7 jours</strong> ; passe ce delai, il faut la renvoyer.
          </p>
          <table className="ressources">
            <thead>
              <tr>
                <th>Adresse</th>
                <th>Role propose</th>
                <th>Envoyee le</th>
                <th>Etat</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invitations.map((i) => (
                <tr key={i.id} className={i.etat === "expiree" ? "retiree" : undefined}>
                  <td className="code">{i.courriel}</td>
                  <td>{LIBELLE_ROLE[i.rolePropose]}</td>
                  <td>{formaterDate(i.creeLe)}</td>
                  <td>
                    {i.etat === "expiree" ? (
                      <strong className="gantt__cache">Expiree le {formaterDate(i.expireLe)}</strong>
                    ) : (
                      <>En attente de reponse — jusqu au {formaterDate(i.expireLe)}</>
                    )}
                  </td>
                  <td className="gestes">
                    {/* Renvoyer = reinviter : la route annule l invitation en cours et en cree
                        une neuve, donc un seul lien reste valable a tout moment. */}
                    <button
                      type="button"
                      className="lien"
                      disabled={enAttente === i.id}
                      title="Envoyer un nouveau lien ; le precedent cesse de valoir"
                      onClick={() => {
                        setEnAttente(i.id);
                        setErreur(null);
                        setMessage(null);
                        inviter(i.courriel, i.rolePropose)
                          .then((r) => {
                            setMessage(r.message);
                            setLien(r.lien);
                            recharger();
                          })
                          .catch((x: unknown) => {
                            setErreur(x instanceof Error ? x.message : String(x));
                          })
                          .finally(() => {
                            setEnAttente(null);
                          });
                      }}
                    >
                      Renvoyer
                    </button>
                    {" · "}
                    <button
                      type="button"
                      className="lien lien--danger"
                      disabled={enAttente === i.id}
                      title="Le lien cesse aussitot de valoir quelque chose"
                      onClick={() => {
                        agir(annulerInvitation(i.id), i.id);
                      }}
                    >
                      Annuler
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h3>Inviter quelqu'un</h3>
      {erreurInvitables !== null ? (
        <p className="alerte">{erreurInvitables}</p>
      ) : invitables === null ? (
        <p>Lecture du référentiel des salariés…</p>
      ) : (
        <>
          <p className="note">
            {invitables.length} personne{invitables.length > 1 ? "s" : ""} invitable
            {invitables.length > 1 ? "s" : ""} : celles dont l'adresse du référentiel ne sert qu'à
            elles. Une adresse de service partagée par plusieurs fiches n'apparaît pas — elle ne
            désigne personne, et un compte demande une adresse propre.
          </p>
          <form
            className="groupes__ajout"
            onSubmit={(e) => {
              e.preventDefault();
              if (choisi === "") return;
              setEnAttente("invitation");
              setErreur(null);
              setMessage(null);
              inviter(choisi, roleInvite)
                .then((r) => {
                  setMessage(r.message);
                  setLien(r.lien);
                  setChoisi("");
                  recharger();
                })
                .catch((x: unknown) => {
                  setErreur(x instanceof Error ? x.message : String(x));
                })
                .finally(() => {
                  setEnAttente(null);
                });
            }}
          >
            <select
              className="saisie"
              value={choisi}
              disabled={enAttente === "invitation" || invitables.length === 0}
              onChange={(e) => {
                setChoisi(e.target.value);
              }}
            >
              <option value="">— choisir une personne —</option>
              {invitables.map((i) => (
                <option key={i.courriel} value={i.courriel}>
                  {i.nom} {i.prenom} — {i.courriel}
                  {i.societe === null ? "" : ` (${i.societe})`}
                </option>
              ))}
            </select>
            <select
              className="saisie"
              value={roleInvite}
              disabled={enAttente === "invitation"}
              onChange={(e) => {
                setRoleInvite(e.target.value as Role);
              }}
            >
              {(Object.keys(LIBELLE_ROLE) as Role[]).map((r) => (
                <option key={r} value={r}>
                  {LIBELLE_ROLE[r]}
                </option>
              ))}
            </select>
            <button type="submit" disabled={choisi === "" || enAttente === "invitation"}>
              {enAttente === "invitation" ? "Envoi…" : "Envoyer l'invitation"}
            </button>
          </form>
          <p className="note">
            Le rôle indiqué ici est une <strong>proposition</strong> : vous le confirmerez en
            approuvant la demande, quand la personne aura créé son mot de passe.
          </p>
        </>
      )}

      {/* Les responsables se nomment ICI, parmi les membres qui viennent d'être listés : c'est
          le même geste d'administration, et aller les chercher dans un autre écran ferait
          oublier que nommer quelqu'un suppose d'abord qu'il ait un compte. */}
      <Responsables membres={membres ?? []} />

      {/* Les destinataires du SAV se posent ici pour la meme raison que les responsables : le
          referentiel MO ne les distingue pas — le Resp Pole Materiel et les mecaniciens portent
          le meme code _MOIAT (mesure du 21/09/2026). */}
      <DestinatairesSav membres={membres ?? []} />
    </section>
  );
}
